import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from '@aimsos/i18n';
import { getAgent } from '../../../../services';
import { parseAgentDetailResponse } from '../../../../shared/utils/apiResponse';
import { mapAgentListItemToUi } from '../../utils';

/**
 * Module-level cache that persists across component unmounts within the same
 * browser session. Stores optimistic mutations applied after a successful save
 * so that the next mount shows the latest values even if the backend has a
 * read-after-write lag.
 *
 * @type {Map<string, Record<string, unknown>>}
 */
const _pendingMutations = new Map();

/**
 * Fetches a single agent by UUID via GET /v1/workspace/agent/:id.
 *
 * Returns a UI-normalised agent record (same shape as list rows) plus
 * loading/error state and a `refetch` helper.
 *
 * @param {string | null | undefined} agentId
 */
export function useAgentDetail(agentId) {
  const { t } = useTranslation('agents');

  const [agent, setAgent] = useState(null);
  /** Start true when we will fetch on mount so the first paint is not "loaded + null agent" (breaks Knowledge tab hooks). */
  const [loading, setLoading] = useState(() => Boolean(agentId && agentId !== 'new'));
  const [error, setError] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  // Reset fetch state when the target changes (render-time adjustment — avoids setState in effect).
  const [prevAgentId, setPrevAgentId] = useState(agentId);
  const [prevRefreshKey, setPrevRefreshKey] = useState(refreshKey);
  if (agentId !== prevAgentId || refreshKey !== prevRefreshKey) {
    setPrevAgentId(agentId);
    setPrevRefreshKey(refreshKey);
    if (agentId && agentId !== 'new') {
      setLoading(true);
      setError(null);
    } else {
      setLoading(false);
      setError(null);
    }
  }

  const refetch = useCallback(() => setRefreshKey((k) => k + 1), []);

  /**
   * Optimistically update the local agent state without a round-trip.
   * Also writes to the module-level cache so the NEXT mount of this hook
   * (after navigation) picks up the values before the API responds.
   */
  const mutate = useCallback(
    (updates) => {
      if (agentId) {
        _pendingMutations.set(agentId, {
          ...(_pendingMutations.get(agentId) ?? {}),
          ...updates,
        });
      }
      setAgent((prev) => (prev ? { ...prev, ...updates } : prev));
    },
    [agentId],
  );

  useEffect(() => {
    if (!agentId || agentId === 'new') return;

    let cancelled = false;

    getAgent(agentId)
      .then((res) => {
        if (cancelled) return;
        const raw = parseAgentDetailResponse(res, t('edit.loadError'));
        const base = mapAgentListItemToUi(raw);
        // Merge any pending optimistic mutations written by a prior save so
        // we never show stale API data when the backend has a write lag.
        const pending = _pendingMutations.get(agentId);
        _pendingMutations.delete(agentId);
        setAgent(pending ? { ...base, ...pending } : base);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message ?? t('common.requestFailed'));
        setAgent(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [agentId, refreshKey, t]);

  return useMemo(
    () => ({ agent, loading, error, refetch, mutate }),
    [agent, loading, error, refetch, mutate],
  );
}
